import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCents, formatCPF, formatMesRef } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ROTULO_TIPO_HOLERITE, type Holerite } from "@/data/folha";
import { AlertTriangle, Download, Info } from "lucide-react";

interface Props {
  holerite: Holerite;
  /** Mostra o botão de imprimir / salvar em PDF. */
  imprimivel?: boolean;
  /** Observações do cálculo para o RH (não aparecem para o empregado). */
  mostrarAvisos?: boolean;
  className?: string;
}

/** Valor sem "R$", como nas colunas de um recibo de pagamento. */
const num = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCnpj = (cnpj: string | null) =>
  cnpj && cnpj.length === 14
    ? cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
    : (cnpj ?? "");

/**
 * Holerite (recibo de pagamento) do CLT, no formato do recibo da
 * contabilidade: rubricas com referência, vencimentos e descontos, totais,
 * líquido e as bases de INSS, FGTS e IRRF. Mostra o documento como foi
 * calculado (ou importado) — nada é recalculado aqui.
 */
export const HoleriteCard = ({ holerite: h, imprimivel = false, mostrarAvisos = false, className }: Props) => {
  const mensal = h.tipo === "mensal" || !h.tipo;
  const titulo = mensal ? `Holerite — ${formatMesRef(h.folha.competencia)}` : ROTULO_TIPO_HOLERITE[h.tipo];
  const campos: [string, string | null][] = [
    ["Código", h.codigoEmpregado],
    ["CPF", h.cpf ? formatCPF(h.cpf) : null],
    ["Cargo", h.cargo],
    ["CBO", h.cbo],
    ["Admissão", h.admissao],
    ["Departamento", h.departamento],
    ["Filial", h.filial],
    ["Situação", h.situacao],
  ];

  return (
    <section
      className={cn(
        "min-w-0 overflow-hidden rounded-xl border bg-card tabular-nums print:rounded-none print:border-0",
        className,
      )}
    >
      {/* Cabeçalho: documento, empregado e empresa */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-[18px] sm:px-[22px]">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="text-base font-semibold first-letter:uppercase">{titulo}</h3>
          <p className="text-xs text-muted-foreground">
            {[h.nome, h.cargo, h.empresaNome].filter(Boolean).join(" · ")}
            {h.empresaCnpj && <> · CNPJ {formatCnpj(h.empresaCnpj)}</>}
          </p>
          <p className="text-xs text-muted-foreground">
            {!mensal && <span className="capitalize">{formatMesRef(h.folha.competencia)}</span>}
            {!mensal && h.dataPagamento && " · "}
            {h.dataPagamento && <>Pagamento em {h.dataPagamento.split("-").reverse().join("/")}</>}
          </p>
        </div>
        {imprimivel && (
          <Button variant="outline" size="sm" className="h-8 print:hidden" onClick={() => window.print()}>
            <Download className="!size-3.5" />
            PDF
          </Button>
        )}
      </div>

      {(h.divergente || (mostrarAvisos && h.avisos)) && (
        <div className="flex flex-col gap-2 px-5 pt-4 print:hidden sm:px-[22px]">
          {h.divergente && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">
                  {h.origem === "extrato" ? "As rubricas não batem com os totais do extrato." : "Holerite com pendência."}
                </p>
                <p className="whitespace-pre-line">{h.divergencias}</p>
              </div>
            </div>
          )}
          {mostrarAvisos && h.avisos && (
            <div className="flex items-start gap-2 rounded-lg border bg-muted-soft p-3 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="whitespace-pre-line">{h.avisos}</p>
            </div>
          )}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 px-5 py-4 text-xs sm:grid-cols-4 sm:px-[22px]">
        {campos
          .filter(([, v]) => v)
          .map(([rotulo, valor]) => (
            <div key={rotulo}>
              <dt className="text-muted-foreground">{rotulo}</dt>
              <dd className="font-medium">{valor}</dd>
            </div>
          ))}
      </dl>

      <div className="overflow-x-auto border-t">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="bg-muted-soft text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            <tr>
              <th className="w-[72px] py-[9px] pl-5 pr-4 text-left font-semibold sm:pl-[22px]">Cód.</th>
              <th className="px-4 py-[9px] text-left font-semibold">Descrição</th>
              <th className="px-4 py-[9px] text-right font-semibold">Referência</th>
              <th className="px-4 py-[9px] text-right font-semibold">Vencimentos</th>
              <th className="py-[9px] pl-4 pr-5 text-right font-semibold sm:pr-[22px]">Descontos</th>
            </tr>
          </thead>
          <tbody>
            {/* Proventos primeiro, como no recibo; dentro de cada grupo, a ordem do extrato. */}
            {[...h.rubricas]
              .sort((a, b) => (a.tipo === b.tipo ? a.ordem - b.ordem : a.tipo === "provento" ? -1 : 1))
              .map((r) => (
                <tr key={r.id} className="border-t border-border-soft">
                  <td className="py-[11px] pl-5 pr-4 text-[13px] text-muted-foreground sm:pl-[22px]">{r.codigo}</td>
                  <td className="px-4 py-[11px]">{r.descricao}</td>
                  <td className="px-4 py-[11px] text-right text-[13px] text-muted-foreground">{r.referencia ?? ""}</td>
                  <td className="px-4 py-[11px] text-right">{r.tipo === "provento" ? num(r.valor) : ""}</td>
                  <td className="py-[11px] pl-4 pr-5 text-right text-destructive sm:pr-[22px]">
                    {r.tipo === "desconto" ? num(r.valor) : ""}
                  </td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr className="border-t bg-muted-soft text-[13px] font-semibold">
              <td className="py-2.5 pl-5 pr-4 sm:pl-[22px]" colSpan={3}>
                Totais
              </td>
              <td className="px-4 py-2.5 text-right">{num(h.proventos)}</td>
              <td className="py-2.5 pl-4 pr-5 text-right text-destructive sm:pr-[22px]">{num(h.descontos)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex items-baseline justify-between gap-3 border-t bg-primary/[0.06] px-5 py-4 sm:px-[22px]">
        <span className="text-[15px] font-semibold">{mensal ? "Líquido a receber" : "Valor líquido"}</span>
        <span className="text-2xl font-semibold text-primary-text">{formatCents(h.liquido)}</span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-t px-5 py-4 text-xs sm:grid-cols-3 sm:px-[22px]">
        {(
          [
            ["Salário base", formatCents(h.salarioBase)],
            ["Sal. contribuição INSS", formatCents(h.baseInss)],
            ["Base cálculo FGTS", formatCents(h.baseFgts)],
            [mensal ? "FGTS do mês" : "FGTS", formatCents(h.valorFgts)],
            ["Base cálculo IRRF", formatCents(h.baseIrrf)],
            ["Dependentes IRRF", String(h.dependentesIrrf)],
          ] as const
        ).map(([rotulo, valor]) => (
          <div key={rotulo}>
            <dt className="text-muted-foreground">{rotulo}</dt>
            <dd className="font-medium">{valor}</dd>
          </div>
        ))}
      </dl>

      {h.observacao && (
        <p className="border-t px-5 py-3 text-xs text-muted-foreground sm:px-[22px]">
          <Badge variant="outline" className="mr-1.5 text-[10px]">
            Obs.
          </Badge>
          {h.observacao}
        </p>
      )}
    </section>
  );
};
